import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Typography, Tabs, Tab, Paper, Button } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LockClockIcon from '@mui/icons-material/LockClock';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BoltIcon from '@mui/icons-material/Bolt';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { useSelector } from 'react-redux';
import { getCookie } from '../../utils/cookies';
import { HotelService } from '../../api/services/hotelService';
import { FinanceService } from '../../api/services/financeService';
import { calculateAuditDates } from '../../utils/dateUtils';
import { isUaeProperty } from '../../utils/dubaiHospitalityTax';
import { getActiveCurrency } from '../../utils/currency';
import NightAuditModal from './components/NightAuditModal';
import FinanceDashboardTab from './components/FinanceDashboardTab';
import FinanceTransactionsTab from './components/FinanceTransactionsTab';
import FinanceExpensesTab from './components/FinanceExpensesTab';
import FinanceNightAuditTab from './components/FinanceNightAuditTab';
import CashierShiftsUI from './components/CashierShiftsUI';
import LiveCashFlowTab from './components/LiveCashFlowTab';

function TabPanel(props) {
    const { children, value, tabKey, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== tabKey}
            id={`finance-tabpanel-${tabKey}`}
            aria-labelledby={`finance-tab-${tabKey}`}
            {...other}
        >
            {value === tabKey && (
                <Box sx={{ pt: 1 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

export default function FinanceManagement() {
    const location = useLocation();
    const navigate = useNavigate();
    const { tenants } = useSelector((state) => state.auth);
    const [auditWarning, setAuditWarning] = useState(null);
    const [auditModalOpen, setAuditModalOpen] = useState(false);

    const authData = useMemo(() => {
        try {
            return JSON.parse(getCookie('adminAuth') || localStorage.getItem('adminAuth') || '{}');
        } catch (_) {
            return {};
        }
    }, []);

    const numericTenantId = authData.tenantId || '1';
    const activeTenant = useMemo(() => {
        return (tenants || []).find(t =>
            String(t.tenantId || t.id || t._id) === String(numericTenantId) ||
            (authData.hotelId && String(t.id || t._id) === String(authData.hotelId))
        );
    }, [tenants, numericTenantId, authData.hotelId]);

    const activeCurrency = getActiveCurrency();
    const isUae = isUaeProperty(activeTenant) || isUaeProperty(authData?.hotel) || activeTenant?.currency === 'AED' || authData?.currency === 'AED' || activeCurrency === 'AED';
    const isIndian = !isUae;

    // Direct tabs configuration matching region requirements
    const tabs = useMemo(() => {
        return [
            { key: 'dashboard', label: 'Dashboard Overview', icon: <DashboardIcon /> },
            { key: 'live-cash-flow', label: 'Live Cash Flow', icon: <SyncAltIcon /> },
            { key: 'transactions', label: 'Transaction History Ledger', icon: <ReceiptLongIcon /> },
            { key: 'expenses', label: 'Operational Expense Ledger', icon: <AccountBalanceIcon /> },
            { key: 'night-audit', label: 'Night Audit Processing', icon: <LockClockIcon /> },
            { key: 'cashier-shifts', label: 'Cashier Shifts', icon: <AccountBalanceWalletIcon /> }
        ];
    }, []);

    // Resilient key mapping supporting legacy numerical indices and string aliases
    const resolveTabKey = useCallback((rawKey) => {
        if (!rawKey) return 'dashboard';
        const clean = String(rawKey).trim().toLowerCase();

        // 1. Exact match with active tab key
        if (tabs.some(t => t.key === clean)) {
            return clean;
        }

        // 2. Named aliases
        const commonAliases = {
            'dashboard': 'dashboard',
            'live-cash-flow': 'live-cash-flow',
            'cash-flow': 'live-cash-flow',
            'cashflow': 'live-cash-flow',
            'transactions': 'transactions',
            'transaction': 'transactions',
            'ledger': 'transactions',
            'expenses': 'expenses',
            'expense': 'expenses',
            'night-audit': 'night-audit',
            'nightaudit': 'night-audit',
            'audit': 'night-audit',
            'cashier-shifts': 'cashier-shifts',
            'cashier': 'cashier-shifts',
            'shifts': 'cashier-shifts'
        };

        if (commonAliases[clean] && tabs.some(t => t.key === commonAliases[clean])) {
            return commonAliases[clean];
        }

        // 3. Numerical index fallback (0, 1, 2, ...)
        if (!isNaN(parseInt(clean, 10))) {
            const idx = parseInt(clean, 10);
            if (tabs[idx]) {
                return tabs[idx].key;
            }
        }

        return 'dashboard';
    }, [tabs]);

    const rawTabParam = new URLSearchParams(location.search).get('tab');
    const activeTab = resolveTabKey(rawTabParam);

    useEffect(() => {
        // Sync URL query param cleanly when opening /finance or resolving alias/index
        if (!rawTabParam || rawTabParam !== activeTab) {
            navigate(`/finance?tab=${activeTab}`, { replace: true });
        }
    }, [rawTabParam, activeTab, navigate]);

    const handleTabChange = (event, newTabKey) => {
        if (!newTabKey || newTabKey === activeTab) return;
        navigate(`/finance?tab=${newTabKey}`);
    };

    const activeHotelId = activeTenant?.id || activeTenant?._id || authData.hotelId || null;

    useEffect(() => {
        if (!activeHotelId) return;
        fetchAuditStatus();
        const handleAuditCompleted = () => {
            fetchAuditStatus();
        };
        window.addEventListener('nightAuditCompleted', handleAuditCompleted);
        return () => {
            window.removeEventListener('nightAuditCompleted', handleAuditCompleted);
        };
    }, [activeHotelId]);

    const fetchAuditStatus = async () => {
        try {
            const authData = JSON.parse(getCookie('adminAuth') || localStorage.getItem('adminAuth') || '{}');
            const numericTenantId = authData.tenantId || '1';

            const activeTenant = (tenants || []).find(t =>
                String(t.tenantId || t.id || t._id) === String(numericTenantId) ||
                (authData.hotelId && String(t.id || t._id) === String(authData.hotelId))
            );
            const hotelUuid = activeTenant?.id || activeTenant?._id || authData.hotelId;
            if (!hotelUuid) return;
            const hotelName = activeTenant?.name || activeTenant?.hotelName || "Your Hotel";

            const res = await HotelService.getById(hotelUuid, numericTenantId);
            const hotel = res?.data || res;

            if (hotel) {
                let lastClosedInput = null;
                try {
                    const historyRes = await FinanceService.getNightAuditHistory({ page: 0, limit: 5 });
                    const historyList = Array.isArray(historyRes?.data) ? historyRes.data : (Array.isArray(historyRes) ? historyRes : []);
                    const latestCompleted = historyList.find(r => ['Completed', 'Balanced', 'Success'].includes(r.status));
                    if (latestCompleted) {
                        lastClosedInput = latestCompleted.date || latestCompleted.createdAt;
                    }
                } catch (historyErr) {
                    console.warn("Could not load night audit history for finance status:", historyErr);
                }

                const auditCalc = calculateAuditDates(lastClosedInput, hotel.businessDate || hotel.createdAt);

                if (auditCalc.diffDays > 0) {
                    setAuditWarning({
                        diffDays: auditCalc.diffDays,
                        hotelName,
                        businessDate: auditCalc.pendingDateFormatted,
                        lastClosedDate: auditCalc.lastClosedFormatted
                    });
                } else {
                    setAuditWarning(null);
                }
            }
        } catch (error) {
            console.error("Failed to fetch hotel audit status for finance management", error);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" fontWeight="bold">Finance Operations</Typography>
            </Box>

            {auditWarning && (
                <Box
                    sx={{
                        mb: 4,
                        p: 3,
                        borderRadius: 4,
                        background: 'linear-gradient(135deg, #fffaf0 0%, #fff0d4 100%)',
                        border: '1px solid rgba(255,183,77,0.3)',
                        boxShadow: '0 8px 32px rgba(237,108,2,0.1)',
                        display: 'flex',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2,
                        position: 'relative',
                        overflow: 'hidden'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <WarningAmberIcon sx={{ color: '#ed6c02', mt: 0.5, fontSize: 32, filter: 'drop-shadow(0 2px 4px rgba(237,108,2,0.4))' }} />
                        <Box>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#b26a00' }}>
                                Night Audit: {auditWarning.diffDays} Hotel Day{auditWarning.diffDays > 1 ? 's' : ''} Pending Closure · {auditWarning.hotelName}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#b26a00', mt: 0.5 }}>
                                Pending closure for <strong>{auditWarning.businessDate}</strong> · Last completed audit: <strong>{auditWarning.lastClosedDate}</strong>.
                                Reports and financial totals remain provisional until caught up.
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<BoltIcon />}
                        onClick={() => setAuditModalOpen(true)}
                        sx={{
                            background: 'linear-gradient(45deg, #d84315 30%, #f4511e 90%)',
                            color: 'white',
                            textTransform: 'none',
                            fontWeight: 'bold',
                            borderRadius: 3,
                            px: 4,
                            py: 1,
                            boxShadow: '0 4px 14px 0 rgba(216, 67, 21, 0.39)',
                            transition: 'all 0.3s ease-in-out',
                            '&:hover': {
                                background: 'linear-gradient(45deg, #bf360c 30%, #d84315 90%)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 6px 20px 0 rgba(216, 67, 21, 0.5)'
                            }
                        }}
                    >
                        Run all {auditWarning.diffDays} pending
                    </Button>
                </Box>
            )}

            <Box sx={{ borderBottom: 1, borderColor: '#eaecf0', mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.925rem',
                            minWidth: 'auto',
                            px: 2.5,
                            py: 1.5,
                            color: '#64748b',
                            '&.Mui-selected': {
                                color: '#4146e0'
                            }
                        },
                        '& .MuiTabs-indicator': {
                            backgroundColor: '#4146e0',
                            height: 2.5,
                            borderRadius: '2px 2px 0 0'
                        }
                    }}
                >
                    {tabs.map((tab) => (
                        <Tab
                            key={tab.key}
                            value={tab.key}
                            label={tab.label}
                            id={`finance-tab-${tab.key}`}
                            aria-controls={`finance-tabpanel-${tab.key}`}
                        />
                    ))}
                </Tabs>
            </Box>

            <Box>
                {/* Dashboard Overview */}
                <TabPanel value={activeTab} tabKey="dashboard">
                    <FinanceDashboardTab />
                </TabPanel>

                {/* Live Cash Flow */}
                <TabPanel value={activeTab} tabKey="live-cash-flow">
                    <LiveCashFlowTab />
                </TabPanel>

                {/* Transaction Ledger */}
                <TabPanel value={activeTab} tabKey="transactions">
                    <FinanceTransactionsTab />
                </TabPanel>

                {/* Operational Expense Ledger (All Properties) */}
                <TabPanel value={activeTab} tabKey="expenses">
                    <FinanceExpensesTab />
                </TabPanel>

                {/* Night Audit Processing */}
                <TabPanel value={activeTab} tabKey="night-audit">
                    <FinanceNightAuditTab />
                </TabPanel>

                {/* Cashier Shifts */}
                <TabPanel value={activeTab} tabKey="cashier-shifts">
                    <CashierShiftsUI />
                </TabPanel>
            </Box>

            <NightAuditModal
                open={auditModalOpen}
                onClose={() => setAuditModalOpen(false)}
                onSuccess={() => {
                    fetchAuditStatus();
                    window.dispatchEvent(new Event('nightAuditCompleted'));
                }}
            />
        </Box>
    );
}
